import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
} from "@mui/material";
import BarChart from "@mui/icons-material/BarChart";
import TrendingUp from "@mui/icons-material/TrendingUp";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import { getter } from "../../functions/axios";

export default function StatsPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await getter("stats");
            if (response && response.data) {
                setStats(response.data);
            } else {
                setError("Failed to load statistics");
            }
        } catch (err) {
            setError("Failed to load statistics: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    const currentDate = new Date();
    const endDate = new Date("2026-02-20");

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                <BarChart sx={{ verticalAlign: "middle", mr: 1 }} />
                Activity Statistics
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
                Overview of QR code activity and usage statistics.
            </Typography>
           { currentDate < endDate && <Alert severity='info'>
                Note that statistics are only available from 20-01-2026 onwards.
            </Alert>}

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                        <TrendingUp color="primary" sx={{ fontSize: 40 }} />
                        <Typography variant="h4" color="primary">
                            {stats?.total_count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Activities
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                        <DownloadIcon color="secondary" sx={{ fontSize: 40 }} />
                        <Typography variant="h4" color="secondary">
                            {stats?.operation_counts?.vcard_download || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            vCard Downloads
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                        <DownloadIcon color="info" sx={{ fontSize: 40 }} />
                        <Typography variant="h4" color="info.main">
                            {stats?.operation_counts?.static_qr_download || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Static QR Downloads
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                        <LinkIcon color="success" sx={{ fontSize: 40 }} />
                        <Typography variant="h4" color="success.main">
                            {stats?.operation_counts?.link_access || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Link Accesses
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Top QR Codes */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Top Accessed QR Codes
                        </Typography>
                        {stats?.top_qr_codes?.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>UID</TableCell>
                                            <TableCell align="right">Accesses</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stats.top_qr_codes.map((qr, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{qr.name}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                        {qr.uid}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{qr.access_count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No QR code accesses recorded yet.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Daily Statistics */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Daily Activity (Last 30 Days)
                        </Typography>
                        {stats?.daily_stats && Object.keys(stats.daily_stats).length > 0 ? (
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">vCard</TableCell>
                                            <TableCell align="right">Static QR</TableCell>
                                            <TableCell align="right">Links</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(stats.daily_stats)
                                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                            .map(([date, counts]) => (
                                                <TableRow key={date}>
                                                    <TableCell>{date}</TableCell>
                                                    <TableCell align="right">
                                                        {counts.vcard_download || 0}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {counts.static_qr_download || 0}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {counts.link_access || 0}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No activity recorded in the last 30 days.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
